import {getTestProps} from '@benchling/js-test-utils';
import {Text, TextInput} from '@mantine/core';
import {StyleSheet, css} from 'aphrodite';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ChatDAO from 'llm/chat-library/ChatDAO';
import {useMessageThread} from 'llm/chat-library/useMessageThread';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT, GRAY, BLUE_POWDER, BLUE, GRAY_DARKER} from 'ui-library/Colors';
import {CenteredSpinner} from 'ui-library/Spinner';
import * as Net from 'util/Net';

interface OnboardingQuestion {
  question: string;
  choices: Array<string>;
}

interface OnboardingAppState {
  is_complete?: boolean;
  questions_asked?: Array<OnboardingQuestion>;
}

function isOnboardingAppState(state: unknown): state is OnboardingAppState {
  if (!state || typeof state !== 'object') {
    return false;
  }
  const s = state as OnboardingAppState;
  return s.questions_asked != null || s.is_complete != null;
}

function FreeTextChoiceButton({
  value,
  onChange,
  onDiscard,
}: {
  value: string;
  onChange: (value: string) => void;
  onDiscard: () => void;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    setIsOpen(true);
    inputRef.current?.focus();
  }, []);

  const handleBlur = useCallback(() => {
    if (!value.trim()) {
      setIsOpen(false);
      onDiscard();
    }
  }, [value, onDiscard]);

  const isSelected = value.trim().length > 0;

  if (isOpen) {
    return (
      <div
        className={css(
          styles.freeTextButton,
          styles.freeTextButtonOpen,
          isSelected && styles.freeTextButtonSelected
        )}
        onClick={() => inputRef.current?.focus()}
        {...getTestProps({component: 'AIOnboardingScreen', element: 'something-else-button'})}
      >
        <span style={{paddingRight: 8, display: 'inline-flex', alignItems: 'center'}}>✏</span>
        <TextInput
          size="md"
          ref={inputRef}
          autoFocus={true}
          variant="unstyled"
          classNames={{input: css(styles.freeTextInput)}}
          styles={{wrapper: {display: 'contents'}}}
          placeholder="Something else"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onBlur={handleBlur}
        />
      </div>
    );
  }

  return (
    <Button
      aria-pressed={isSelected}
      buttonStyle={isSelected ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_WHITE}
      size={ButtonSize.MEDIUM}
      className={css(styles.freeTextButton)}
      icon={<span style={{paddingRight: 8, display: 'inline-flex', alignItems: 'center'}}>✏</span>}
      text="Something else"
      onClick={handleClick}
      dataTestAttrs={{component: 'AIOnboardingScreen', element: 'something-else-button'}}
    />
  );
}
const SKIPPED_ANSWER = '[skipped]';

export function CyclingAnswersText({answers}: {answers: Array<string>}): JSX.Element | null {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
    if (answers.length <= 1) {
      return;
    }
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % answers.length);
    }, 1800);
    return () => {
      clearInterval(id);
    };
  }, [answers]);

  if (answers.length === 0) {
    return null;
  }

  return <Text className={css(styles.loadingAnswerText)}>"{answers[index]}"</Text>;
}

function QuestionView({
  chatSessionId,
  questions,
  questionGroupIndex,
  onAnswersSubmitted,
  onQuestionIndexChange,
}: {
  chatSessionId: string;
  questions: Array<OnboardingQuestion>;
  /** Zero-based index of this question group within the entire onboarding flow (increments each time the backend sends a new batch). */
  questionGroupIndex: number;
  onAnswersSubmitted: (answers: Array<string>) => void;
  onQuestionIndexChange: (index: number) => void;
}): JSX.Element {
  const [batchIndex, setBatchIndex] = useState(0);

  useEffect(() => {
    onQuestionIndexChange(batchIndex);
  }, [batchIndex, onQuestionIndexChange]);
  const [batchAnswers, setBatchAnswers] = useState<Array<string>>([]);
  const [selectedChoices, setSelectedChoices] = useState<Set<string>>(new Set());
  const [freeTextValue, setFreeTextValue] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentQuestion = questions[batchIndex] ?? null;
  const freeTextTrimmed = freeTextValue.trim();
  const effectiveAnswer = [...selectedChoices, ...(freeTextTrimmed ? [freeTextTrimmed] : [])].join(', ');

  const canContinue = !isSubmitting && effectiveAnswer.length > 0;

  const handleSelectChoice = useCallback((choice: string) => {
    setSelectedChoices((prev) => {
      const next = new Set(prev);
      if (next.has(choice)) {
        next.delete(choice);
      } else {
        next.add(choice);
      }
      return next;
    });
  }, []);

  const handleDiscardFreeText = useCallback(() => {
    setFreeTextValue('');
  }, []);

  const submitAnswers = useCallback(
    async (answers: Array<string>) => {
      setIsSubmitting(true);
      onAnswersSubmitted(answers);
      try {
        const messageText = questions.map((q, i) => `${q.question}: ${answers[i] ?? ''}`).join('\n');
        await ChatDAO.appendDisplayMessageToChatSession({
          chatSessionId,
          agentType: AgentType.ONBOARDING,
          messageBlocks: [{type: 'text', text: messageText}],
          userChatContext: ChatDAO.getUserChatContext({contextIds: []}),
          sources: [],
        });
      } catch (err) {
        if (!Net.isServerRequestError(err)) {
          throw err;
        }
        setIsSubmitting(false);
      }
    },
    [chatSessionId, questions, onAnswersSubmitted]
  );

  const handleAdvance = useCallback(
    async (answer: string) => {
      const isSkipped = answer === SKIPPED_ANSWER;

      if (isSkipped) {
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SKIPPED_ONBOARDING_QUESTION, {
          question_index_in_group: batchIndex,
          question_group_index: questionGroupIndex,
          question_group_size: questions.length,
          num_choices: currentQuestion.choices.length,
        });
      } else {
        const hasFreeText = freeTextValue.trim().length > 0;
        const selectedChoiceCount = selectedChoices.size;
        trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.ANSWERED_ONBOARDING_QUESTION, {
          question_index_in_group: batchIndex,
          question_group_index: questionGroupIndex,
          question_group_size: questions.length,
          num_choices: currentQuestion.choices.length,
          selected_choice_count: selectedChoiceCount,
          has_free_text_answer: hasFreeText,
          total_answer_count: selectedChoiceCount + (hasFreeText ? 1 : 0),
        });
      }

      const updatedAnswers = [...batchAnswers, answer];
      const nextIndex = batchIndex + 1;

      if (nextIndex < questions.length) {
        setBatchAnswers(updatedAnswers);
        setBatchIndex(nextIndex);
        setSelectedChoices(new Set());
        setFreeTextValue('');
        return;
      }

      await submitAnswers(updatedAnswers);
    },
    [
      batchAnswers,
      batchIndex,
      questions,
      questionGroupIndex,
      freeTextValue,
      selectedChoices,
      submitAnswers,
      currentQuestion,
    ]
  );

  if (!currentQuestion) {
    return <></>;
  }

  return (
    <div className={css(styles.body)}>
      <h2 className={css(styles.questionHeading)}>{currentQuestion.question}</h2>
      <div
        className={css(styles.choicesGrid)}
        {...getTestProps({component: 'AIOnboardingScreen', element: 'choices'})}
      >
        {currentQuestion.choices.map((choice) => (
          <Button
            key={choice}
            aria-pressed={selectedChoices.has(choice)}
            buttonStyle={
              selectedChoices.has(choice) ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_WHITE
            }
            size={ButtonSize.MEDIUM}
            growToFitContents={true}
            text={choice}
            onClick={() => {
              handleSelectChoice(choice);
            }}
          />
        ))}
        <FreeTextChoiceButton
          key={currentQuestion.question}
          value={freeTextValue}
          onChange={setFreeTextValue}
          onDiscard={handleDiscardFreeText}
        />
      </div>
      <div className={css(styles.continueRow)}>
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MEDIUM}
          text="Continue"
          onClick={() => effectiveAnswer && handleAdvance(effectiveAnswer)}
          disabled={!canContinue}
          dataTestAttrs={{component: 'AIOnboardingScreen', element: 'continue-button'}}
        />
        <Button
          buttonStyle={ButtonStyle.TEXT}
          size={ButtonSize.SMALL}
          text="Skip"
          onClick={() => handleAdvance(SKIPPED_ANSWER)}
          disabled={isSubmitting}
          dataTestAttrs={{component: 'AIOnboardingScreen', element: 'skip-button'}}
        />
      </div>
    </div>
  );
}

/**
 * Onboarding screen driven directly by useMessageThread.
 *
 * Thread status drives all UI states:
 *  - isAssistantRunning (QUEUED/ACTIVE) → loading spinner
 *  - isAwaitingUserInput (AWAITING_USER_INPUT) → question UI sourced from latestApplicationState
 *  - errorMessage → error + skip UI
 */
export function AIOnboardingScreen({
  chatSessionId,
  onDismiss,
}: {
  chatSessionId: string;
  onDismiss: () => void;
}): JSX.Element {
  const {
    isLoading,
    isAssistantRunning,
    isAwaitingUserInput,
    isCompleted,
    latestApplicationState,
    errorMessage,
  } = useMessageThread({chatSessionId});

  const appState = isOnboardingAppState(latestApplicationState) ? latestApplicationState : null;

  const [submittedAnswers, setSubmittedAnswers] = useState<Array<string>>([]);

  // Track how many question batches have been shown (increments each time a new batch arrives).
  const [questionGroupIndex, setQuestionGroupIndex] = useState(0);
  const lastQuestionsRef = useRef<Array<OnboardingQuestion> | null>(null);
  const totalQuestionsShownRef = useRef(0);
  const currentQuestionIndexInGroupRef = useRef(0);

  const handleQuestionIndexChange = useCallback((index: number) => {
    currentQuestionIndexInGroupRef.current = index;
  }, []);

  // Questions are available in app state while AWAITING_USER_INPUT.
  const currentQuestions = useMemo((): Array<OnboardingQuestion> | null => {
    if (!isAwaitingUserInput || !appState?.questions_asked?.length) {
      return null;
    }
    return appState.questions_asked;
  }, [isAwaitingUserInput, appState]);

  // Increment the question group counter when a new batch of questions arrives.
  useEffect(() => {
    if (currentQuestions == null || currentQuestions === lastQuestionsRef.current) {
      return;
    }
    if (lastQuestionsRef.current != null) {
      setQuestionGroupIndex((prev) => prev + 1);
    }
    totalQuestionsShownRef.current += currentQuestions.length;
    currentQuestionIndexInGroupRef.current = 0;
    lastQuestionsRef.current = currentQuestions;
  }, [currentQuestions]);

  // Track when the onboarding screen is first shown.
  useEffect(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_ONBOARDING_SCREEN);
  }, []);

  // When the thread completes, fire a completion event then transition to the settings page.
  useEffect(() => {
    if (isCompleted) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.COMPLETED_ONBOARDING);
      onDismiss();
    }
  }, [isCompleted, onDismiss]);

  const handleDismiss = useCallback(() => {
    if (!isCompleted) {
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.DISMISSED_ONBOARDING_SCREEN, {
        question_group_index: questionGroupIndex,
        question_index_in_group: currentQuestionIndexInGroupRef.current,
        questions_shown_total: totalQuestionsShownRef.current,
      });
    }
    onDismiss();
  }, [isCompleted, onDismiss, questionGroupIndex]);

  const showLoading = isLoading || isAssistantRunning || isCompleted;
  const hasPreviousQuestions = (appState?.questions_asked?.length ?? 0) > 0;
  const loadingMessage = hasPreviousQuestions
    ? 'Processing your answers…'
    : 'Starting onboarding and gathering information…';

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'AIOnboardingScreen', element: 'root'})}
    >
      <div className={css(styles.exitRow)}>
        <Button
          aria-label="Exit onboarding"
          buttonStyle={ButtonStyle.TRANSPARENT}
          size={ButtonSize.SMALL}
          icon="close"
          onClick={handleDismiss}
          dataTestAttrs={{component: 'AIOnboardingScreen', element: 'exit-button'}}
        />
      </div>

      {showLoading && (
        <div className={css(styles.body)}>
          <div className={css(styles.loadingState)}>
            <CenteredSpinner />
            <Text className={css(styles.loadingText)}>{loadingMessage}</Text>
            {submittedAnswers.length > 0 && <CyclingAnswersText answers={submittedAnswers} />}
          </div>
        </div>
      )}

      {!showLoading && errorMessage && (
        <div className={css(styles.body)}>
          <div className={css(styles.errorState)}>
            <p className={css(styles.errorText)}>{errorMessage}</p>
            <Button
              buttonStyle={ButtonStyle.TEXT}
              size={ButtonSize.SMALL}
              text="Exit"
              onClick={handleDismiss}
              dataTestAttrs={{component: 'AIOnboardingScreen', element: 'error-exit-button'}}
            />
          </div>
        </div>
      )}

      {!showLoading && !errorMessage && isAwaitingUserInput && currentQuestions && (
        <QuestionView
          chatSessionId={chatSessionId}
          questions={currentQuestions}
          questionGroupIndex={questionGroupIndex}
          onAnswersSubmitted={setSubmittedAnswers}
          onQuestionIndexChange={handleQuestionIndexChange}
        />
      )}
    </div>
  );
}

const PADDING = 16;

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxWidth: 650 + PADDING * 2,
    width: '100%',
    margin: '0 auto',
    padding: `8px ${PADDING}px 16px`,
  },
  exitRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '4px',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
  },
  questionHeading: {
    fontSize: '18px',
    fontWeight: 700,
    lineHeight: '24px',
    margin: '0 0 8px',
  },
  choicesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  freeTextButton: {
    borderStyle: 'dashed',
  },
  freeTextButtonOpen: {
    display: 'inline-flex',
    alignItems: 'center',
    border: `1px dashed ${GRAY_LIGHT}`,
    borderRadius: '4px',
    padding: '5px 12px',
    cursor: 'text',
  },
  freeTextButtonSelected: {
    background: BLUE_POWDER,
    borderColor: BLUE_POWDER,
    color: BLUE,
  },
  freeTextInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '13px',
    width: '160px',
    padding: 0,
  },
  loadingState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 0',
  },
  loadingText: {
    fontSize: '13px',
    color: GRAY_DARKER,
    textAlign: 'center',
    margin: 0,
  },
  loadingAnswerText: {
    fontSize: '13px',
    color: GRAY_DARKER,
    textAlign: 'center',
    margin: 0,
    maxWidth: '280px',
    wordWrap: 'break-word',
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 0',
  },
  errorText: {
    fontSize: '13px',
    color: GRAY,
    textAlign: 'center',
    margin: 0,
  },
  continueRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    paddingTop: '16px',
  },
});
